import React, { useState, useEffect } from 'react';
import { Save, RotateCcw, Mail } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import { apiGet, apiRequest } from '../../utils/api';
import { TEMPLATE_STATUSES, PLACEHOLDERS, getRawTemplate } from './useAuditEmailTemplate';
import toast from 'react-hot-toast';

const ClientEmailTemplateSettings = ({ onClose }) => {
  const { theme } = useTheme();
  const d = theme === 'dark';
  const [templates, setTemplates] = useState({});
  const [activeKey, setActiveKey] = useState('scheduled');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    apiGet('/client/audit_tracker/email_templates')
      .then((r) => r.ok ? r.json() : { templates: {} })
      .then((data) => setTemplates(data.templates || {}))
      .catch(() => {});
  }, []);

  // Load template for active key
  useEffect(() => {
    const raw = getRawTemplate(activeKey, templates);
    setSubject(raw.subject_template);
    setBody(raw.body_template);
    setDirty(false);
  }, [activeKey, templates]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const updated = {
        ...templates,
        [activeKey]: { subject_template: subject, body_template: body },
      };
      const res = await apiRequest('/client/audit_tracker/email_templates', { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ templates: updated }) });
      if (res.ok) {
        const data = await res.json();
        setTemplates(data.templates || updated);
        setDirty(false);
        toast.success('Template saved');
      } else {
        const data = await res.json();
        toast.error(data.error || 'Failed to save');
      }
    } catch (err) { toast.error(err.message); }
    finally { setSaving(false); }
  };

  const handleReset = () => {
    // Reset to built-in default (remove client override for this key)
    const updated = { ...templates };
    delete updated[activeKey];
    setTemplates(updated);
    // The useEffect will reload the default
  };

  const insertPlaceholder = (placeholder) => {
    setBody((prev) => prev + placeholder);
    setDirty(true);
  };

  return (
    <div className={`rounded-xl border ${d ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'} shadow-lg`}>
      {/* Header */}
      <div className={`flex items-center justify-between px-5 py-3 border-b ${d ? 'border-gray-700' : 'border-gray-200'}`}>
        <div className="flex items-center gap-2">
          <Mail size={16} className="text-blue-500" />
          <span className="text-sm font-dm-serif">Email Templates</span>
        </div>
        {onClose && (
          <button onClick={onClose} className={`text-xs font-rasa px-3 py-1 rounded-lg ${d ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-gray-100 hover:bg-gray-200 text-gray-600'}`}>
            Close
          </button>
        )}
      </div>

      <div className="flex">
        {/* Status tabs (left sidebar) */}
        <div className={`w-48 shrink-0 border-r py-2 ${d ? 'border-gray-700' : 'border-gray-200'}`}>
          {TEMPLATE_STATUSES.map((s) => {
            const isCustom = !!templates[s.key];
            return (
              <button
                key={s.key}
                onClick={() => setActiveKey(s.key)}
                className={`w-full text-left px-4 py-2 text-[12px] font-rasa transition-colors ${
                  activeKey === s.key
                    ? (d ? 'bg-gray-700 text-white' : 'bg-blue-50 text-blue-700')
                    : (d ? 'text-gray-400 hover:bg-gray-700/50' : 'text-gray-600 hover:bg-gray-50')
                }`}
              >
                {s.label}
                {isCustom && <span className="ml-1.5 text-[9px] text-blue-500 font-bold">custom</span>}
              </button>
            );
          })}
        </div>

        {/* Editor */}
        <div className="flex-1 p-5 space-y-4">
          {/* Placeholder chips */}
          <div>
            <div className={`text-[10px] font-rasa font-bold uppercase mb-1.5 ${d ? 'text-gray-500' : 'text-gray-400'}`}>
              Insert Placeholder
            </div>
            <div className="flex flex-wrap gap-1.5">
              {PLACEHOLDERS.map((p) => (
                <button
                  key={p.key}
                  onClick={() => insertPlaceholder(p.key)}
                  className={`text-[10px] font-rasa px-2 py-1 rounded border transition-colors ${
                    d ? 'border-gray-600 text-gray-400 hover:bg-gray-700 hover:text-gray-200' : 'border-gray-300 text-gray-500 hover:bg-gray-100 hover:text-gray-700'
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          {/* Subject */}
          <div>
            <label className={`text-[10px] font-rasa font-bold uppercase ${d ? 'text-gray-400' : 'text-gray-500'}`}>Subject</label>
            <input
              value={subject}
              onChange={(e) => { setSubject(e.target.value); setDirty(true); }}
              className={`w-full mt-1 px-3 py-2 rounded-lg border text-sm font-rasa ${d ? 'bg-gray-700 border-gray-600 text-gray-200' : 'bg-white border-gray-300 text-gray-700'} focus:outline-none focus:ring-1 focus:ring-blue-500`}
            />
          </div>

          {/* Body */}
          <div>
            <label className={`text-[10px] font-rasa font-bold uppercase ${d ? 'text-gray-400' : 'text-gray-500'}`}>Body</label>
            <textarea
              value={body}
              onChange={(e) => { setBody(e.target.value); setDirty(true); }}
              rows={10}
              className={`w-full mt-1 px-3 py-2 rounded-lg border text-sm font-rasa leading-relaxed ${d ? 'bg-gray-700 border-gray-600 text-gray-200' : 'bg-white border-gray-300 text-gray-700'} focus:outline-none focus:ring-1 focus:ring-blue-500 resize-y`}
            />
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2">
            <button
              onClick={handleSave}
              disabled={!dirty || saving}
              className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-rasa bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50"
            >
              <Save size={13} /> {saving ? 'Saving...' : 'Save Template'}
            </button>
            <button
              onClick={handleReset}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-rasa ${d ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-gray-100 hover:bg-gray-200 text-gray-600'}`}
            >
              <RotateCcw size={13} /> Reset to Default
            </button>
            {templates[activeKey] && (
              <span className={`text-[11px] font-rasa ml-2 ${d ? 'text-blue-400' : 'text-blue-600'}`}>Using custom template</span>
            )}
            {!templates[activeKey] && (
              <span className={`text-[11px] font-rasa ml-2 ${d ? 'text-gray-500' : 'text-gray-400'}`}>Using default template</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClientEmailTemplateSettings;
